/**
 * Sawyer 08 meds-feeding (A1+G10) — `/meds-feeding?date=&service=`: the day's
 * meds & feeding list on its own page, reached from the Day list's and the
 * Dashboard's "Meds & feeding" buttons (lane 02's seam, lib/meds-feeding-
 * link.ts) and from the med pass. The list itself is `MedsFeedingReport`,
 * the same component the Reports tab shows.
 *
 * The URL owns the date, the service filter and (from the Day list) the
 * service-type id the server resolves, so a refresh or a shared link keeps
 * the view. Gated by ROUTE_ACCESS_RULES['/meds-feeding'] (`pets:read`: the
 * six care roles, never the accountant).
 */
import { Link, useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { todayIsoDate } from '../lib/api.js';
import type { CareDayService } from '../lib/meds-feeding-api.js';
import { MedsFeedingReport } from '../components/meds-feeding/MedsFeedingReport.js';
import { dayListHref, formatDayLabel, isIsoDate, parseDayView, shiftIsoDate } from '../components/dashboard/day-list-filters.js';
import './calendar-day.css';

function parseService(raw: string | null): CareDayService {
  return raw === 'boarding' || raw === 'daycare' ? raw : 'all';
}

export function MedsFeeding({ config }: { config: ClientConfig }): JSX.Element {
  const [params, setParams] = useSearchParams();
  const rawDate = params.get('date');
  const date = isIsoDate(rawDate) ? rawDate : todayIsoDate();
  const service = parseService(params.get('service'));
  const serviceTypeId = params.get('serviceTypeId');
  const from = params.get('from');

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next);
  };

  const back =
    from === 'day-list'
      ? { to: dayListHref({ date, serviceTypeId, view: parseDayView(params.get('view')) }), label: '← Day list' }
      : from === 'dashboard'
        ? { to: '/dashboard', label: '← Dashboard' }
        : null;

  const dateControl = (
    <div className="ferro-calday__nav">
      <button
        type="button"
        className="ferro-btn ferro-btn--ghost ferro-calday__nav-btn"
        onClick={() => update({ date: shiftIsoDate(date, -1) })}
        aria-label="Previous day"
        data-testid="meds-feeding-prev"
      >
        ‹
      </button>
      <div className="ferro-calday__date">
        <p className="ferro-calday__title" data-testid="meds-feeding-date">
          {formatDayLabel(date)}
          {date === todayIsoDate() ? <span className="ferro-calday__today"> · Today</span> : null}
        </p>
        <label className="ferro-calday__picker">
          <span className="ferro-calday__sr">Pick a date</span>
          <input
            type="date"
            value={date}
            onChange={(e) => {
              if (isIsoDate(e.target.value)) update({ date: e.target.value });
            }}
            data-testid="meds-feeding-date-input"
          />
        </label>
      </div>
      <button
        type="button"
        className="ferro-btn ferro-btn--ghost ferro-calday__nav-btn"
        onClick={() => update({ date: shiftIsoDate(date, 1) })}
        aria-label="Next day"
        data-testid="meds-feeding-next"
      >
        ›
      </button>
    </div>
  );

  return (
    <section className="ferro-admin-page ferro-mf-page" data-testid="meds-feeding-page">
      {back ? (
        <Link className="ferro-btn ferro-btn--ghost ferro-mf-btn ferro-mf-back" to={back.to} data-testid="meds-feeding-back">
          {back.label}
        </Link>
      ) : null}
      <MedsFeedingReport
        apiUrl={config.deployment.apiUrl}
        date={date}
        service={service}
        serviceTypeId={service === 'all' ? serviceTypeId : null}
        onServiceChange={(s) => update({ service: s, serviceTypeId: null })}
        dateControl={dateControl}
        titleAs="h1"
      />
    </section>
  );
}
